import { type CustomIconName } from '@/components/Icon/Icon';
import { type MessageId } from '@/assets/lang/en-US';
import type {
  CustomListSummary,
  Span,
  NetworkGroupType,
  GroupSummary,
  NetworkGroupData,
  FuelHazardRating,
} from '@/generated/types/navigator/types.gen';
import { WorkLogStatus } from '@/generated/types/navigator/types.gen';
import type { Feature, Geometry as GeoJsonGeometry } from 'geojson';

// Opaque group id: an integer NetworkGroup id, or a topology GroupType uuid when the flag is on.
export type GroupId = NetworkGroupData['id'];

type AnalysisFeatureScores = Pick<
  GroupSummary,
  | 'totalEncScore'
  | 'totalPriorityScore'
  | 'totalWildfirePriorityScore'
  | 'criticalTreesCount'
  | 'hazardTreesCount'
  | 'clearSpanCount'
  | 'clearPercentage'
  | 'wildfireRisk'
>;

export type TagField = {
  id: number;
  name: string;
};

export type AnalysisFeatureProperties = AnalysisFeatureScores & {
  group?: string;
  networkGroupId?: GroupId;
  level2?: string;
  encMatrixId?: number;
  bbox?: string;
  workStatus?: WorkLogStatus;
  tags?: string;
  fuelHazardRating?: FuelHazardRating;
};

export type AnalysisFeature = Feature<
  GeoJsonGeometry,
  AnalysisFeatureProperties
>;

export const DISTRIBUTION_CLIENT = 'distribution';
export const TRANSMISSION_CLIENT = 'transmission';
export const MULTI_CLIENT = 'multi';

export type MetricUnit = 'ft' | 'm' | 'mi' | 'km';

export enum NetworkType {
  Distribution = 1,
  Transmission,
}

export type PhaseType = 'single-phase' | 'two-phase' | 'three-phase';

export type LevelType = 'level1' | 'level2';

export enum NETWORK_GROUP {
  level3 = 'level3',
  level2 = 'level2',
}

export enum Rating {
  Unknown = 0,
  None,
  Minor,
  Low,
  Medium,
  High,
  // Critical,
}

export const FUEL_HAZARD_RATINGS: Rating[] = [
  Rating.High,
  Rating.Medium,
  Rating.Low,
  Rating.Minor,
  Rating.None,
];

export enum WildfireRiskRating {
  Unknown = 0,
  Very_low,
  Low,
  Medium,
  High,
  Very_high,
}

export enum WorktypeAnalysisRating {
  Error = 0,
  Not_Vegetation,
  Herbicide_Zone,
  Mow_Zone,
  Cut_Zone,
  Side_Wall,
}

export enum PriorityRatingType {
  Unknown = 999,
  P1 = 1,
  P2,
  P3,
  P4,
  P5,
}

enum EncroachmentType {
  with_overhang = 'with_overhang',
  without_overhang = 'without_overhang',
}

export const WITH_OVERHANG = EncroachmentType.with_overhang;
export const WITHOUT_OVERHANG = EncroachmentType.without_overhang;

export const getWildfireRiskText = (
  wildfireRiskRating: WildfireRiskRating
): string => {
  switch (wildfireRiskRating) {
    case WildfireRiskRating.Very_high:
      return 'Very high';
    case WildfireRiskRating.High:
      return 'High';
    case WildfireRiskRating.Medium:
      return 'Medium';
    case WildfireRiskRating.Low:
      return 'Low';
    case WildfireRiskRating.Very_low:
      return 'Very low';
    case WildfireRiskRating.Unknown:
      return 'Unknown';
    default:
      return 'Unknown';
  }
};

export const WORK_TYPE_MESSAGE_IDS: Record<WorktypeAnalysisRating, MessageId> =
  {
    [WorktypeAnalysisRating.Error]: 'workType.noWorkType',
    [WorktypeAnalysisRating.Not_Vegetation]: 'workType.noWorkType',
    [WorktypeAnalysisRating.Herbicide_Zone]: 'workType.herbicide',
    [WorktypeAnalysisRating.Mow_Zone]: 'workType.mow',
    [WorktypeAnalysisRating.Cut_Zone]: 'workType.cut',
    [WorktypeAnalysisRating.Side_Wall]: 'workType.sideWall',
  };

export const getPriorityRatingText = (
  priorityRating: PriorityRatingType
): string => {
  switch (priorityRating) {
    case PriorityRatingType.P1:
      return 'P1';
    case PriorityRatingType.P2:
      return 'P2';
    case PriorityRatingType.P3:
      return 'P3';
    case PriorityRatingType.P4:
      return 'P4';
    case PriorityRatingType.P5:
      return 'P5';
    default:
      return 'Unknown';
  }
};

export type Summary = {
  total: number;
  totalLength: number;
  metric: MetricUnit;
  categoriesByEncroachmentScore?: number[];
  categoriesByPriorityScore?: number[];
  categoriesByWildfirePriorityScore?: number[];
  categoriesByWildfireRisk?: number[];
  categoriesByCriticalTreesCount?: number[];
  categoriesByClear?: number[];
  bbox?: number[];
};

export type ApiResponse<T, S> = {
  summary: S;
  data: T[];
};

export type EncroachmentClientConfig = {
  clientId: number;
  id: number;
  config: EncroachmentBoundaries;
};

type EncroachmentBoundaries = {
  boundaries_unit: string;
  horizontal_boundaries: number[];
  vertical_boundaries: number[];
};

export type NetworkGroupConfig = {
  type: NetworkGroupType;
  networkGroupId: GroupId;
};

export class HttpNotFoundError extends Error {
  constructor(msg: string) {
    super(msg);

    // Set the prototype explicitly.
    Object.setPrototypeOf(this, HttpNotFoundError.prototype);
  }
}

/** The client-context request could not resolve the client, delivery, or release named in the URL. */
export class ClientContextNotFoundError extends HttpNotFoundError {
  constructor(msg: string) {
    super(msg);
    Object.setPrototypeOf(this, ClientContextNotFoundError.prototype);
  }
}

export class HttpUnauthorizedError extends Error {
  constructor(detail: string) {
    super(detail);
    Object.setPrototypeOf(this, HttpUnauthorizedError.prototype);
  }
}

export const getWorklogStatusText = (status: WorkLogStatus): string => {
  switch (status) {
    case WorkLogStatus.COMPLETED:
      return 'Work completed';
    case WorkLogStatus.NO_WORK_NEEDED:
      return 'No work needed';
    case WorkLogStatus.WORK_NEEDED:
      return 'Work needed';
    default:
      return 'No work activity';
  }
};

export const WorkLogStatusIconMap = new Map<WorkLogStatus, CustomIconName>([
  [WorkLogStatus.COMPLETED, 'WorkCompleted'],
  [WorkLogStatus.NO_WORK_NEEDED, 'NoWorkNeeded'],
  [WorkLogStatus.WORK_NEEDED, 'WorkNeeded'],
]);

/**
 * WKT (Well-Known Text) format for bounding box
 *
 * Could utilize a library for this if it gets out of hand.
 */
export type BoundingBoxString =
  `BOX(${number}.${number} ${number}.${number}, ${number}.${number} ${number}.${number})`;

export interface ScanData {
  scan_id: string;
  label: string;
  started_at: string;
  created_at: string;
  client_id: number;
}

export type SatelliteImageMetadata = {
  path: string;
  name: string;
  provider: string;
  date: string;
  bbox: string;
  layerName: string;
  showImageryDate: boolean;
};

export type CustomListBasics = {
  listId: string;
  name: string;
};

// Defined so that downstream users don't need to gate on id's presence
export type SavedCustomList = CustomListSummary & { id: string };

export type SavedListResponse = {
  data: Span[];
  summary: SavedCustomList;
};

export const ViewModeOptions = {
  CUSTOM_LIST_DETAIL: 'CUSTOM_LIST_DETAIL',
  CUSTOM_LIST_OVERVIEW: 'CUSTOM_LIST_OVERVIEW',
  CUSTOM_LIST_SPAN: 'CUSTOM_LIST_SPAN',
  LEGACY: 'LEGACY',
  UNINITIALIZED: 'UNINITIALIZED',
} as const;

export type ViewModeOptions = keyof typeof ViewModeOptions;

export const ProjectFilteringViewModes: ViewModeOptions[] = [
  ViewModeOptions.CUSTOM_LIST_DETAIL,
  ViewModeOptions.CUSTOM_LIST_SPAN,
];

type ProjectDetails = {
  id: string;
  name: string;
};

export type ViewMode = {
  mode: ViewModeOptions;
  modeDetails: ProjectDetails | null; // Union other mode details as needed
};
